import { useEffect } from 'react';
import ParticleField from '@/components/base/ParticleField';
import Header from '@/components/feature/Header';
import HeroSection from './components/HeroSection';
import PrecursorSection from './components/PrecursorSection';
import AlphaSigmaSection from './components/AlphaSigmaSection';
import WhitePapersSection from './components/WhitePapersSection';
import ContactSection from './components/ContactSection';

export default function Home() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Fieldscale Technologies Ltd',
        alternateName: 'Fieldscale',
        url: siteUrl,
        logo: 'https://static.readdy.ai/image/e56119d9701c4940d688cc54fccf122e/60ae1d1cc04e0b663bfc83b27abd0288.png',
        description: 'Fieldscale Technologies builds governed intelligence systems. GenesisOS is the first lifecycle operating system for AI agents with compiled identity, persistent memory, and deterministic governance.',
        foundingDate: '2024',
        industry: 'Artificial Intelligence',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '71-75 Shelton Street, Covent Garden',
          addressLocality: 'London',
          postalCode: 'WC2H 9JQ',
          addressCountry: 'GB',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'inquiry@fieldscale.io',
          contactType: 'Customer Service',
        },
        sameAs: [],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Governed Intelligence Systems | Fieldscale Technologies',
        url: siteUrl,
        description: 'Fieldscale Technologies builds governed intelligence systems. GenesisOS is the first lifecycle operating system for AI agents with compiled identity, persistent memory, and deterministic governance.',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteUrl}?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'GenesisOS',
        description: 'The first lifecycle operating system for AI agents with compiled identity, persistent memory, and deterministic governance. Agents that remember, learn, and stay the same across sessions, channels, and providers.',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Cloud',
        brand: { '@type': 'Brand', name: 'Fieldscale Technologies Ltd' },
        offers: { '@type': 'Offer', availability: 'https://schema.org/PreOrder' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Precursor',
        description: 'A deterministic market intelligence engine powered by governed execution architecture. AI auditability and deterministic governance for enterprise intelligence.',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Cloud',
        brand: { '@type': 'Brand', name: 'Fieldscale Technologies Ltd' },
      },
    ];

    const injected: HTMLScriptElement[] = [];
    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      script.setAttribute('data-schema', 'home');
      document.head.appendChild(script);
      injected.push(script);
    });

    return () => {
      injected.forEach((s) => s.remove());
    };
  }, []);

  return (
    <div className="relative min-h-screen bg-black overflow-x-hidden">
      <ParticleField />
      <Header />
      
      <main className="relative z-10">
        <HeroSection />
        <AlphaSigmaSection />
        <WhitePapersSection />
        <PrecursorSection />
        <ContactSection />
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-12 border-t border-white/10">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="w-8 h-8 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/e56119d9701c4940d688cc54fccf122e/60ae1d1cc04e0b663bfc83b27abd0288.png" 
                  alt="Fieldscale Logo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <span className="text-white font-bold text-xl tracking-wide">Fieldscale Technologies Ltd.</span>
            </div>
            
            <div className="flex items-center">
              <p className="text-white/60 text-sm">© {new Date().getFullYear()} Fieldscale Technologies Ltd. • UK Company No. 16734531 • All rights reserved</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
